import { useContext, useEffect, useMemo } from 'react'
import { type IntlShape, useIntl } from 'react-intl'
import useGlobalSearchContext from 'hooks/GlobalSearch/useGlobalSearchContext'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchStateActions, GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'

import { getSearchItemFromURLSearchParams } from 'lib/url/searchUrlUtils'
import GeoContext from 'contexts/geoContext'
import config from 'constants/config'
import Places from 'constants/places'
function formatCruisePopularDestinationLabel(intl: IntlShape, label: string): string {
  switch (label) {
    case 'All destinations':
      return intl.formatMessage({
        id: '-qgRQjjfmKNy-5IWBx3J',
        defaultMessage: 'All destinations',
      })
    case 'Australia':
      return intl.formatMessage({
        id: 'zcetI3IWYJdy6pNbYDOy',
        defaultMessage: 'Australia',
      })
    case 'New Zealand':
      return intl.formatMessage({
        id: 'nsOq-fgb-3aCdx867QSF',
        defaultMessage: 'New Zealand',
      })
    case 'Hawaii':
      return intl.formatMessage({
        id: 'G1UE0riBjMrI7XCkxag2',
        defaultMessage: 'Hawaii',
      })
    case 'Spain':
      return intl.formatMessage({
        id: 'v3TRrCPjxoVfck_v997d',
        defaultMessage: 'Spain',
      })
    case 'Italy':
      return intl.formatMessage({
        id: 'YqUaxiGB4BF68V0NJ2-l',
        defaultMessage: 'Italy',
      })
    case 'Singapore':
      return intl.formatMessage({
        id: 'a-6KjVLIk_Wm3kM7g2YA',
        defaultMessage: 'Singapore',
      })
    case 'United States of America':
      return intl.formatMessage({
        id: 'JDc-8EMhzx-viFMN6tHd',
        defaultMessage: 'United States of America',
      })
    case 'Fiji':
      return intl.formatMessage({
        id: 'j3khlKYfhymvvzBGz8II',
        defaultMessage: 'Fiji',
      })
    default:
      return label
  }
}

export const popularDestinations: Array<App.SearchItem> = [
  {
    value: Places.Anywhere.id,
    format: {
      mainText: 'All destinations',
    },
    searchType: 'destination',
  },
  {
    format: {
      mainText: 'Australia',
    },
    value: 'le_d3d9446802a44259755d38e6d163e820',
    searchType: 'destination',
  },
  {
    format: {
      mainText: 'New Zealand',
    },
    value: 'le_9fc3d7152ba9336a670e36d0ed79bc43',
    searchType: 'destination',
  },
  {
    format: {
      mainText: 'Hawaii',
    },
    value: 'le_979d472a84804b9f647bc185a877a8b5',
    searchType: 'destination',
  },
  {
    format: {
      mainText: 'Spain',
    },
    value: 'le_7e7757b1e12abcb736ab9a754ffb617a',
    searchType: 'destination',
  },
  {
    format: {
      mainText: 'Italy',
    },
    value: 'le_93db85ed909c13838ff95ccfa94cebd9',
    searchType: 'destination',
  },
  {
    format: {
      mainText: 'Singapore',
    },
    value: 'le_bd4c9ab730f5513206b999ec0d90d1fb',
    searchType: 'destination',
  },
  {
    format: {
      mainText: 'United States of America',
    },
    value: 'le_757b505cfd34c64c85ca5b5690ee5293',
    searchType: 'destination',
  },
  {
    format: {
      mainText: 'Fiji',
    },
    value: 'le_72b32a1f754ba1c09b3695e0cb6cde7f',
    searchType: 'destination',
  },
]

export function useGlobalCruisesSearchContext(
  initialState: GlobalSearchState = GLOBAL_SEARCH_INITIAL_STATE,
  syncWithURLSearchParams?: URLSearchParams,
) {
  const intl = useIntl()
  const localizedPopularDestinations = useMemo<Array<App.SearchItem>>(() => popularDestinations.map(destination => ({
    ...destination,
    format: {
      ...destination.format,
      mainText: formatCruisePopularDestinationLabel(intl, destination.format.mainText),
    },
  })), [intl])

  const queryParams = useMemo(() => {
    if (syncWithURLSearchParams) {
      return {
        searchItem: getSearchItemFromURLSearchParams(syncWithURLSearchParams),
      }
    }
    return undefined
  }, [syncWithURLSearchParams])

  const { globalSearchState, globalSearchDispatch } = useGlobalSearchContext({
    ...initialState,
    popularDestinations: localizedPopularDestinations,
    isLoadingPopularDestinations: false,
    searchVerticals: new Set(['cruises']),
  })

  // sync search item
  useEffect(() => {
    if (queryParams) {
      const { searchItem } = queryParams
      if (searchItem) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_SEARCH_ITEM })
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.searchItem])

  return {
    globalCruisesSearchDispatch: globalSearchDispatch,
    globalCruisesSearchState: globalSearchState,
  }
}

export const useCruiseSearchEnabled = () => {
  const { currentRegionCode } = useContext(GeoContext)
  return useMemo(() =>
    config.CRUISES_REGIONS?.split(',').includes(currentRegionCode),
  [currentRegionCode])
}
